/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';
import { logger } from '@yorkie-js/sdk/src/util/logger';
import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';
import { escapeString } from '@yorkie-js/sdk/src/document/json/strings';
import type * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { DataSize } from '@yorkie-js/sdk/src/util/resource';
import {
  bigintToBytesLE,
  bigintFromBytesLE,
  bigintFromBytesLEUnsigned,
  isWithinInt64Range,
} from '@yorkie-js/sdk/src/util/number';

export enum PrimitiveType {
  Null,
  Boolean,
  Integer,
  Long,
  Double,
  String,
  Bytes,
  Date,
}

/**
 * `PrimitiveValue` represents a value of primitive type. Only values of type
 * included in `PrimitiveValue` can be set to the document.
 */
export type PrimitiveValue =
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  null | boolean | number | bigint | string | Uint8Array | Date;

/**
 * `Primitive` represents primitive data type including logical clock.
 * It has a type and a value.
 */
export class Primitive extends CRDTElement {
  private valueType: PrimitiveType;
  private value: PrimitiveValue;

  constructor(value: PrimitiveValue, createdAt: TimeTicket) {
    super(createdAt);
    this.valueType = Primitive.getPrimitiveType(value)!;
    if (this.valueType === PrimitiveType.Long) {
      // Both the number path (promoted from out-of-int32 integers) and the
      // bigint path converge here. Reject magnitudes outside the int64 range
      // instead of silently wrapping them in `bigintToBytesLE`, since the SDK
      // has no wider lossless integer type to promote them to.
      const longValue =
        typeof value === 'number' ? BigInt(value) : (value as bigint);
      if (!isWithinInt64Range(longValue)) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          `${value} is out of the int64 range and cannot be stored as Long`,
        );
      }
      if (typeof value === 'number') {
        logger.warn(
          `number exceeds int32 range and will be stored as Long (bigint): ${value}`,
        );
      }
      this.value = longValue;
    } else {
      this.value = value === undefined ? null : value;
    }
  }

  /**
   * `of` creates a new instance of Primitive.
   */
  public static of(value: PrimitiveValue, createdAt: TimeTicket): Primitive {
    return new Primitive(value, createdAt);
  }

  /**
   * `valueFromBytes` parses the given bytes into value.
   */
  public static valueFromBytes(
    primitiveType: PrimitiveType,
    bytes: Uint8Array,
  ): PrimitiveValue {
    switch (primitiveType) {
      case PrimitiveType.Null:
        return null;
      case PrimitiveType.Boolean:
        return bytes[0] ? true : false;
      case PrimitiveType.Integer:
        return bytes[0] | (bytes[1] << 8) | (bytes[2] << 16) | (bytes[3] << 24);
      case PrimitiveType.Double: {
        // NOTE(chacha912): The bytes are copied into a buffer of this value's
        // own before being read. The earlier code copied them to offset 0 of
        // the *underlying* buffer and read them back from there, which
        // answered correctly and wrote through to whatever else was sharing
        // that buffer: a snapshot arrives as one buffer the decoder hands out
        // as views, so reading a double out of a document overwrote the first
        // eight bytes of the snapshot itself.
        //
        // Reading in place would fix that but would also make a payload
        // shorter than eight bytes throw, where it used to answer. The copy
        // keeps both: nothing outside this value is written, and a short
        // payload reads as the zero-padded value it would have been.
        const buffer = new Uint8Array(8);
        buffer.set(bytes.subarray(0, 8));
        return new DataView(buffer.buffer).getFloat64(0, true);
      }
      case PrimitiveType.String:
        return new TextDecoder('utf-8').decode(bytes);
      case PrimitiveType.Long:
        return bigintFromBytesLE(bytes);
      case PrimitiveType.Bytes:
        // NOTE(chacha912): Copied for the same reason as the Double case above:
        // the decoder hands out views into one snapshot buffer, and returning
        // this one would hand the application a window onto the rest of that
        // buffer, which a later decode is free to write through.
        return bytes.slice();
      case PrimitiveType.Date:
        return new Date(Number(bigintFromBytesLEUnsigned(bytes)));
      default:
        throw new YorkieError(
          Code.ErrUnimplemented,
          `unimplemented type: ${primitiveType}`,
        );
    }
  }

  /**
   * `getValueSize` returns the size of the value. The size is similar to
   * the size of primitives in JavaScript.
   */
  private getValueSize(): number {
    switch (this.valueType) {
      case PrimitiveType.Null:
        // Word Size in 64-bit system.
        return 8;
      case PrimitiveType.Boolean:
        return 4;
      case PrimitiveType.Integer:
        return 4;
      case PrimitiveType.Long:
        return 8;
      case PrimitiveType.Double:
        return 8;
      case PrimitiveType.String:
        // string is UTF-16 encoded.
        return (this.value as string).length * 2;
      case PrimitiveType.Bytes:
        return (this.value as Uint8Array).length;
      case PrimitiveType.Date:
        return 8;
      default:
        throw new YorkieError(
          Code.ErrUnimplemented,
          `unimplemented type: ${this.valueType}`,
        );
    }
  }

  /**
   * `getDataSize` returns the data usage of this element.
   */
  public getDataSize(): DataSize {
    return {
      data: this.getValueSize(),
      meta: this.getMetaUsage(),
    };
  }

  /**
   * `toJSON` returns the JSON encoding of the value.
   */
  public toJSON(): string {
    if (this.valueType === PrimitiveType.String) {
      return `"${escapeString(this.value as string)}"`;
    }
    if (this.valueType === PrimitiveType.Bytes) {
      const bytes = this.value as Uint8Array;
      return `"${btoa(String.fromCharCode(...bytes))}"`;
    }
    if (this.valueType === PrimitiveType.Date) {
      return `"${(this.value as Date).toISOString()}"`;
    }

    return `${this.value}`;
  }

  /**
   * `toSortedJSON` returns the sorted JSON encoding of the value.
   */
  public toSortedJSON(): string {
    return this.toJSON();
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    return {
      createdAt: this.getCreatedAt().toTestString(),
      value: this.value,
      type: 'YORKIE_PRIMITIVE',
    };
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  public deepcopy(): Primitive {
    const primitive = Primitive.of(this.value, this.getCreatedAt());
    primitive.setMovedAt(this.getMovedAt());
    primitive.setRemovedAt(this.getRemovedAt());
    return primitive;
  }

  /**
   * `getType` returns the type of the value.
   */
  public getType(): PrimitiveType {
    return this.valueType!;
  }

  /**
   * `getPrimitiveType` returns the primitive type of the value.
   */
  public static getPrimitiveType(value: unknown): PrimitiveType | undefined {
    switch (typeof value) {
      case 'undefined':
        return PrimitiveType.Null;
      case 'boolean':
        return PrimitiveType.Boolean;
      case 'number':
        if (this.isInteger(value)) {
          if (value > Math.pow(2, 31) - 1 || value < -Math.pow(2, 31)) {
            return PrimitiveType.Long;
          }
          return PrimitiveType.Integer;
        }
        return PrimitiveType.Double;
      case 'bigint':
        return PrimitiveType.Long;
      case 'string':
        return PrimitiveType.String;
      case 'object':
        if (value === null) {
          return PrimitiveType.Null;
        } else if (value instanceof Uint8Array) {
          return PrimitiveType.Bytes;
        } else if (value instanceof Date) {
          return PrimitiveType.Date;
        }
    }

    return;
  }

  /**
   * `isSupport` check if the given value is supported type.
   */
  public static isSupport(value: unknown): boolean {
    const primitiveType = Primitive.getPrimitiveType(value);
    if (primitiveType === undefined) {
      return false;
    }
    return true;
  }

  /**
   * `isInteger` checks if the given number is integer.
   */
  public static isInteger(num: number): boolean {
    return num % 1 === 0;
  }

  /**
   * `isNumericType` checks numeric type by JSONPrimitive
   */
  public isNumericType(): boolean {
    const t = this.valueType;
    return (
      t === PrimitiveType.Integer ||
      t === PrimitiveType.Long ||
      t === PrimitiveType.Double
    );
  }

  /**
   * `getValue` returns the value of Primitive.
   */
  public getValue(): PrimitiveValue {
    return this.value;
  }

  /**
   * `toBytes` creates an array representing the value.
   */
  public toBytes(): Uint8Array {
    switch (this.valueType) {
      case PrimitiveType.Null: {
        return new Uint8Array();
      }
      case PrimitiveType.Boolean: {
        const boolVal = this.value as boolean;
        return boolVal ? new Uint8Array([1]) : new Uint8Array([0]);
      }
      case PrimitiveType.Integer: {
        const intVal = this.value as number;
        return new Uint8Array([
          intVal & 0xff,
          (intVal >> 8) & 0xff,
          (intVal >> 16) & 0xff,
          (intVal >> 24) & 0xff,
        ]);
      }
      case PrimitiveType.Double: {
        const doubleVal = this.value as number;
        const uint8Array = new Uint8Array(8);
        const view = new DataView(uint8Array.buffer);
        view.setFloat64(0, doubleVal, true);
        return uint8Array;
      }
      case PrimitiveType.String: {
        return new TextEncoder().encode(this.value as string);
      }
      case PrimitiveType.Long: {
        return bigintToBytesLE(this.value as bigint);
      }
      case PrimitiveType.Bytes: {
        const bytesVal = this.value as Uint8Array;
        return bytesVal;
      }
      case PrimitiveType.Date: {
        const dateVal = this.value as Date;
        return bigintToBytesLE(BigInt(dateVal.getTime()));
      }
      default:
        throw new YorkieError(
          Code.ErrUnimplemented,
          `unimplemented type: ${this.valueType}`,
        );
    }
  }
}
